// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@west/App/Store/Reducers";
import ProductShareLink, { ProductShareLinkStyles } from "@west/Overrides/Components/ProductShareLink";
import { xOpenPrintDialog } from "@west/XDL/Utilities";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    xGoogleAnalytics: (state as CustomAppState).xGoogleAnalytics,
});

export interface ProductDetailsShareLinkStyles {
    wrapper?: InjectableCss;
    link?: ProductShareLinkStyles;
    // XNG-Changes
    printLink?: LinkPresentationProps;
}

export const shareLinkStyles: ProductDetailsShareLinkStyles = {
    wrapper: {
        css: css`
            margin-top: 30px;
            text-align: center;
        `,
    },
};

const styles = shareLinkStyles;

const ProductDetailsShareLink: React.FC<Props> = ({ product, xGoogleAnalytics }) => {
    if (!product) {
        return null;
    }

    // XNG-Changes: Google Analytics
    const gaPdpActionLinksClickHandler = (label?: string, isShare = false) => {
        const pdpGaEvent = isShare ? xGoogleAnalytics.pdpShareEvent : xGoogleAnalytics.pdpPrintEvent;
        const pdpGaEventName = isShare ? xGoogleAnalytics.pdpShareEventName : xGoogleAnalytics.pdpPrintEventName;
        if (pdpGaEvent) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: pdpGaEventName,
                click_text: label,
                item_id: product.id,
                item_name: product.productTitle,
                affiliation: xGoogleAnalytics.affiliation,
                authentication_state: xGoogleAnalytics.authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };
    const onPrintDialogue = () => {
        xOpenPrintDialog();
        gaPdpActionLinksClickHandler(translate("Print"));
    };

    // XNG-Changes: Update Structure
    return (
        <StyledWrapper {...styles.wrapper}>
            {createWidgetElement("ProductDetails/ProductDetailsAddToListLink", { fields: {} })}
            {/* XNG-Changes: passed googleAnalytics from here so it applies to pdp only */}
            <ProductShareLink
                text={translate("Share Product")}
                GoogleAnalyticsOnSuccessHandler={gaPdpActionLinksClickHandler}
                extendedStyles={styles.link}
            />
            <Link onClick={onPrintDialogue} {...styles.printLink} aria-label={translate("Print")}>
                {translate("Print")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsShareLink)),
    definition: {
        displayName: "Action Links",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
